import { Box } from "@mui/material";
import { useIsMobile } from "common/utils";
import { MapView } from "features/mapLayers";
import { MutableRefObject, ReactNode } from "react";
import { MapRef } from "react-map-gl/mapbox";
import { useLocation } from "react-router-dom";
import { shouldShowMap } from "../routes";
import DesktopLayout from "./DesktopLayout";
import MobileLayout from "./MobileLayout";

interface AppLayoutProps {
  children: ReactNode;
  mapRef: MutableRefObject<MapRef | null>;
}

/**
 * Check if current route is a public route (login, signup, password reset)
 * These routes should not show the app layout (drawer, map, bottom nav)
 */
function useIsPublicRoute() {
  const location = useLocation();
  const publicRoutes = [
    "/login",
    "/signup",
    "/signup-intro",
    "/login/password-forgot",
    "/login/password-reset",
    // /invite/:token resolves before auth; hide the app shell so the drawer
    // doesn't flash for unauthenticated farmers. The landing page itself is
    // mounted as a top-level <Route> in App.tsx so it still renders here.
    "/invite",
  ];
  return publicRoutes.some(
    (route) =>
      location.pathname === route || location.pathname.startsWith(route + "/")
  );
}

/**
 * Main layout wrapper that selects the appropriate layout based on platform.
 *
 * Desktop: Uses flex layout where:
 * - Drawer (nav sidebar) is fixed width on the left
 * - Secondary panel (content) is fixed width next to drawer (when map is shown)
 * - Map fills the remaining space to the right
 *
 * Mobile: MapView is rendered as a full-screen sibling (unchanged)
 *
 * IMPORTANT: This component ALWAYS renders to maintain consistent hook ordering
 * across route transitions. For public routes, the content is hidden but the
 * component tree structure is preserved.
 */
export default function AppLayout({ children, mapRef }: AppLayoutProps) {
  const isMobile = useIsMobile();
  const isPublicRoute = useIsPublicRoute();
  const location = useLocation();
  const showMap = shouldShowMap(location.pathname);

  // Always render to maintain consistent hook ordering.
  // Hide content for public routes but keep component tree stable.
  if (isMobile) {
    // Mobile: Keep existing architecture with full-screen map as sibling
    return (
      <Box sx={{ display: isPublicRoute ? "none" : "contents" }}>
        <MobileLayout mapRef={mapRef}>{children}</MobileLayout>
        <MapView mapRef={mapRef} />
      </Box>
    );
  }

  // Desktop: Flex layout with map positioned to the right of sidebars
  return (
    <Box
      sx={{
        display: isPublicRoute ? "none" : "flex",
        height: "100vh",
        width: "100vw",
        overflow: "hidden",
      }}
    >
      {/* Drawer + Content panels */}
      <DesktopLayout mapRef={mapRef}>{children}</DesktopLayout>

      {/* Map - only visible on map routes, positioned to the right of sidebars */}
      {/* Negative margin allows map to overlap panel so rounded corners are visible */}
      {showMap && (
        <Box
          sx={{
            flex: 1,
            height: "100vh",
            position: "relative",
            marginLeft: "-16px", // Overlap to show panel's rounded corners
          }}
        >
          <MapView mapRef={mapRef} />
        </Box>
      )}
    </Box>
  );
}
